/**
 * DirectionSelectionHandler
 * DIRECTION Entity 타입의 선택 핸들러 로직
 */

import type { SelectionStrategy } from '../../base/selectionStrategy'
import type { BaseSelectionHandler } from '../../base/baseSelectionHandler'
import { CANVAS_ENTITY_EVENT, SHAPES } from '../../../const'

export class DirectionSelectionHandler implements SelectionStrategy {
  /**
   * DIRECTION 타입 선택 핸들러 추가
   * @param entity - BaseSelectionHandler 인스턴스
   */
  addHandlers(entity: BaseSelectionHandler): void {
    const systemLayer = entity.canvasEngine.getRenderer().layers.system
    if (!systemLayer) {
      throw new Error('시스템 레이어가 없습니다.')
    }

    const shape = entity.getShape()
    if (!shape) {
      throw new Error('도형이 없습니다.')
    }

    // Transformer 초기화
    entity.destroyTransformer()
    const target = shape

    const interactionEnabled =
      entity.getEntityInteractionMode() && entity.isSelected && !entity.canvasEngine.isPinchZooming

    // 드래그 가능하게 설정
    target.draggable(interactionEnabled)

    // hit stroke 끄기 — 원본은 hitStrokeWidth(parseInt(hitStrokeWidth()))로 'auto'를 NaN으로 만들었고
    // Konva hasHitStroke()는 NaN·0을 같이 false로 본다(중심점 원은 stroke가 없어 hit 변화 없음)
    target.hitArea({ kind: 'strokeWidth', px: 0 })

    // Transformer 생성 (DIRECTION은 회전 활성화)
    const shapeTransformer = entity.createSelectionHandles(
      {
        id: 'single-selection',
        padding: 20,
        anchorCornerRadius: 20, // DIRECTION 전용
        anchorSize: 20, // DIRECTION 전용
        rotateEnabled: interactionEnabled, // 회전 핸들 표시
        resizeEnabled: false,
        borderStrokeWidth: 2,
        ignoreStroke: true,
        borderHitStrokeWidth: entity.TRANSFORMER_HIT_STROKE_WIDTH,
        draggable: interactionEnabled,
        anchorDraggable: interactionEnabled,
        anchorHitStrokeWidth: entity.TRANSFORMER_HIT_STROKE_WIDTH,
      },
      [target],
    )
    entity.setShapeTransformer(shapeTransformer)
    entity.canvasEngine.initShapeTransformer(shapeTransformer)

    // 회전 관련 변수
    let rotationOrigin: { x: number; y: number } | null = null
    let lastRotation = 0

    // Transform Start 이벤트 (회전 초기화)
    const eventManager = entity.getEventManager()
    eventManager.removeEvent(target, CANVAS_ENTITY_EVENT.SELECT_TRANSFORM_START)
    eventManager.addEvent(target, CANVAS_ENTITY_EVENT.SELECT_TRANSFORM_START, () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) return
      entity.removeShapeAnimation()

      // Direction 회전 초기화
      if (entity.entityType === SHAPES.DIRECTION) {
        lastRotation = 0
        rotationOrigin = target.position()
      }
    })

    // Transform 이벤트 (회전 처리)
    shapeTransformer.off('transform')
    shapeTransformer.on('transform', () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) {
        // 멀티터치 중이면 transform 중단
        shapeTransformer.stopTransform()
        // 회전 상태 리셋
        if (rotationOrigin) {
          target.position(rotationOrigin)
          target.rotation(0)
        }
        lastRotation = 0
        return
      }

      if (entity.entityType === SHAPES.DIRECTION && rotationOrigin) {
        entity.canvasEngine.disableAnotherEntityListening(entity.getId())
        // Transformer가 핸들 위치를 변경하지 못하도록 고정
        target.position(rotationOrigin)

        // 45도 스냅 회전
        const currentRotation = target.rotation()
        const snappedRotation = Math.round(currentRotation / 45) * 45
        const delta = snappedRotation - lastRotation

        if (Math.abs(delta) > 0) {
          // rotateDirection 메서드 호출
          const directionEntity = entity as BaseSelectionHandler & {
            rotateDirection: (delta: number, origin: { x: number; y: number }) => void
          }
          directionEntity.rotateDirection(delta, rotationOrigin)
          lastRotation = snappedRotation
        }
        target.rotation(snappedRotation)
      }
    })

    // Transform End 이벤트 (회전 종료)
    eventManager.removeEvent(target, CANVAS_ENTITY_EVENT.SELECT_TRANSFORM_END)
    eventManager.addEvent(target, CANVAS_ENTITY_EVENT.SELECT_TRANSFORM_END, () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) return

      // Direction은 rotation 초기화
      if (entity.entityType === SHAPES.DIRECTION) {
        if (rotationOrigin) target.position(rotationOrigin)
        target.rotation(0) // 좌표 직접 수정 후 transformer rotation 초기화
        lastRotation = 0
        rotationOrigin = null
      }

      entity.canvasEngine.restoreAllEntitiyListening()
      entity.addShapeAnimation()
      entity.updateBalloonPositionPublic()
      entity.canvasEngine.events.onEntityTransform(entity.getId())
      entity.showButtonGroup()
      entity.updateButtonPosition()
    })

    // Drag Start 이벤트
    let lastPos: { x: number; y: number } | null = null
    eventManager.removeEvent(target, CANVAS_ENTITY_EVENT.SELECT_DRAG_START)
    eventManager.addEvent(target, CANVAS_ENTITY_EVENT.SELECT_DRAG_START, () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) {
        // 멀티터치 중이면 드래그 중단
        if (target.isDragging()) {
          target.stopDrag()
        }
        lastPos = null
        return
      }
      lastPos = target.position()

      // 파동 애니메이션 모드에 따른 처리
      if (entity.balloon) {
        if (!entity.getRippleFollowMode()) {
          entity.removeShapeAnimation()
        }
      }
    })

    // Drag Move 이벤트 (Direction 전용 로직)
    eventManager.removeEvent(target, CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE)
    eventManager.addEvent(target, CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE, () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) return

      // Direction은 절대좌표를 사용하므로 별도 처리
      if (entity.entityType === SHAPES.DIRECTION) {
        if (!lastPos) return
        const currentPos = target.position()
        const dx = currentPos.x - lastPos.x
        const dy = currentPos.y - lastPos.y
        const directionEntity = entity as BaseSelectionHandler & {
          moveDirection: (dx: number, dy: number) => void
        }
        directionEntity.moveDirection(dx, dy)
        lastPos = currentPos
      }

      // 파동 애니메이션 따라다니기 모드
      if (entity.balloon && entity.getRippleFollowMode()) {
        entity.updateRipplePosition()
      }

      entity.hideButtonGroup()
    })

    // Drag End 이벤트
    eventManager.removeEvent(target, CANVAS_ENTITY_EVENT.SELECT_DRAG_END)
    eventManager.addEvent(target, CANVAS_ENTITY_EVENT.SELECT_DRAG_END, () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) return

      lastPos = null

      // 파동 애니메이션 복원
      if (entity.balloon && !entity.getRippleFollowMode()) {
        entity.addShapeAnimation()
      }

      entity.updateBalloonPositionPublic()
      entity.canvasEngine.events.onEntityTransform(entity.getId())
      entity.showButtonGroup()
      entity.updateButtonPosition()
    })

    // 시스템 레이어에 Transformer 추가
    systemLayer.add(shapeTransformer.node)
    systemLayer.batchDraw()
  }
}
